import { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import {
    Box, Typography, Button, Stack, Chip, CircularProgress,
    Avatar, Alert, Select, Option,
} from '@mui/joy';
import { motion, AnimatePresence } from 'framer-motion';

import ArrowBackRoundedIcon      from '@mui/icons-material/ArrowBackRounded';
import ArrowForwardRoundedIcon   from '@mui/icons-material/ArrowForwardRounded';
import AddRoundedIcon            from '@mui/icons-material/AddRounded';
import DeleteRoundedIcon         from '@mui/icons-material/DeleteRounded';
import CheckRoundedIcon          from '@mui/icons-material/CheckRounded';
import LocationOnRoundedIcon     from '@mui/icons-material/LocationOnRounded';
import PersonRoundedIcon         from '@mui/icons-material/PersonRounded';
import AccessTimeRoundedIcon     from '@mui/icons-material/AccessTimeRounded';
import CheckCircleRoundedIcon    from '@mui/icons-material/CheckCircleRounded';
import CalendarTodayRoundedIcon  from '@mui/icons-material/CalendarTodayRounded';
import StarRoundedIcon           from '@mui/icons-material/StarRounded';

import { AppContext } from '../../context';
import { businessTodayISO } from '../../businessDay';
import {
    APIGetLocations,
    APIGetAvailableForDate,
    APIGetDayAssignments,
    APICreateAssignments,
    APIConfirmDay,
    APIDeleteAssignment,
    APIGetWeekSummary,
    parseApiError,
} from '../../api';
import Header from '../../components/Header';
import MemeLoader from '../../components/MemeLoader';
import ManagerScheduleEditModal from './ManagerScheduleEditModal';
import TimeSelect5 from '../../components/TimeSelect5';



const DAYS_SHORT = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];

const getWeekStart = (offset = 0) => {
    const now = new Date();
    const day = now.getDay();
    const diff = day === 0 ? -6 : 1 - day;   // понедельник этой недели
    const mon = new Date(now);
    mon.setDate(now.getDate() + diff + offset * 7);
    mon.setHours(0, 0, 0, 0);
    return mon;
};

const ROLE_META = {
    photographer:       { label: 'Фотограф',   color: 'var(--joy-palette-primary-300)', bg: 'rgba(162,38,226,0.15)' },
    admin:              { label: 'Админ',       color: '#3b82f6',                        bg: 'rgba(59,130,246,0.15)' },
    admin_photographer: { label: 'Админ+Фото',  color: '#f59e0b',                        bg: 'rgba(245,158,11,0.15)' },
};
const roleMeta = (r) => ROLE_META[r] || ROLE_META.photographer;

const getWeekDays = (start) =>
    Array.from({ length: 7 }, (_, i) => {
        const d = new Date(start);
        d.setDate(d.getDate() + i);
        return d;
    });

const fmtRange = (days) => `${days[0].getDate()} ${MONTHS_RU[days[0].getMonth()]} — ${days[6].getDate()} ${MONTHS_RU[days[6].getMonth()]}`;

// ─── Утилиты ──────────────────────────────────────────────────────────────────
const DAYS_FULL  = ['Воскресенье', 'Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота'];
const MONTHS_RU  = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];

const fmtDateFull = (iso) => {
    if (!iso) return '';
    const d = new Date(iso + 'T00:00:00');
    return `${DAYS_FULL[d.getDay()]}, ${d.getDate()} ${MONTHS_RU[d.getMonth()]}`;
};

const toISO = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

// Ближайшие 14 дней
const getUpcomingDates = () =>
    Array.from({ length: 14 }, (_, i) => {
        const d = new Date();
        d.setDate(d.getDate() + i);
        return toISO(d);
    });

const getInitials = (name = '') => {
    const p = name.trim().split(' ');
    return (p[0]?.[0] || '') + (p[1]?.[0] || '');
};

// ─── Анимации ─────────────────────────────────────────────────────────────────
const pageVariants = {
    initial: { opacity: 0, x: 24 },
    animate: { opacity: 1, x: 0, transition: { duration: 0.38, ease: [0.22, 1, 0.36, 1] } },
    exit:    { opacity: 0, x: 24, transition: { duration: 0.2 } },
};

const cardAnim = {
    hidden:  { opacity: 0, y: 14 },
    visible: (i = 0) => ({
        opacity: 1, y: 0,
        transition: { delay: i * 0.06, duration: 0.3, ease: [0.22, 1, 0.36, 1] },
    }),
};

// ─── Компонент: выбор даты по неделям (как на дашборде) ───────────────────────
function WeekPicker({ weekDays, label, summary, selectedDate, onSelect }) {
    const today = toISO(new Date());

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.2 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--joy-palette-text-secondary)' }}>
                    {label}
                </Typography>
                <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)' }}>
                    {fmtRange(weekDays)}
                </Typography>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: { xs: 0.5, sm: 1 } }}>
                {weekDays.map((date) => {
                    const iso      = toISO(date);
                    const isToday  = iso === today;
                    const active   = iso === selectedDate;
                    const isPast   = date < new Date(new Date().setHours(0, 0, 0, 0));
                    const filled   = summary[iso]?.available || 0;
                    const total    = summary[iso]?.assigned  || 0;
                    const byRole = summary[iso]?.byRole || { photographer: 0, admin: 0, admin_photographer: 0 };

                    return (
                        <Box
                            key={iso}
                            onClick={() => onSelect(iso)}
                            sx={{
                                borderRadius: 'var(--joy-radius-md)',
                                border:       '1.5px solid',
                                borderColor:  active
                                    ? 'var(--joy-palette-primary-500)'
                                    : isToday
                                        ? 'rgba(162,38,226,0.4)'
                                        : 'var(--joy-palette-divider)',
                                bgcolor:      active
                                    ? 'rgba(162,38,226,0.15)'
                                    : isToday
                                        ? 'rgba(162,38,226,0.05)'
                                        : isPast
                                            ? 'transparent'
                                            : 'var(--joy-palette-background-level1)',
                                p:            { xs: 0.5, sm: 1 },
                                textAlign:    'center',
                                cursor:       'pointer',
                                opacity:      isPast && !active ? 0.5 : 1,
                                transition:   'all .15s',
                                '&:hover':    { borderColor: 'var(--joy-palette-primary-400)', bgcolor: 'rgba(162,38,226,0.07)' },
                            }}
                        >
                            <Typography sx={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: active ? 'var(--joy-palette-primary-400)' : 'var(--joy-palette-text-tertiary)' }}>
                                {DAYS_SHORT[date.getDay()]}
                            </Typography>
                            <Typography sx={{ fontSize: { xs: 14, sm: 16 }, fontWeight: 800, color: active ? 'var(--joy-palette-primary-300)' : 'var(--joy-palette-text-primary)', lineHeight: 1.2, my: 0.3 }}>
                                {date.getDate()}
                            </Typography>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3, mt: 0.4 }}>
                                {filled > 0 && (
                                    <Box sx={{ display: 'flex', gap: 0.3, justifyContent: 'center', flexWrap: 'wrap' }}>
                                        {[
                                            { n: byRole.photographer,       ...roleMeta('photographer') },
                                            { n: byRole.admin,               ...roleMeta('admin') },
                                            { n: byRole.admin_photographer,  ...roleMeta('admin_photographer') },
                                        ].filter(x => x.n > 0).map((x, i) => (
                                            <Box key={i} sx={{ fontSize: { xs: 8, sm: 10 }, fontWeight: 800, color: x.color, bgcolor: x.bg, borderRadius: '4px', px: { xs: 0.3, sm: 0.5 }, py: 0.1, minWidth: 14, textAlign: 'center' }}>
                                                {x.n}
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                                {total > 0 && (
                                    <Box sx={{ fontSize: { xs: 7, sm: 9 }, fontWeight: 700, color: '#22c55e', bgcolor: 'rgba(34,197,94,0.1)', borderRadius: '4px', px: { xs: 0.3, sm: 0.5 }, py: 0.2, whiteSpace: 'nowrap' }}>
                                        {total} назн.
                                    </Box>
                                )}
                                {filled === 0 && total === 0 && (
                                    <Box sx={{ fontSize: 9, color: 'var(--joy-palette-text-tertiary)' }}>—</Box>
                                )}
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}

// Обёртка: всегда текущая + следующая неделя
function DatePicker({ selectedDate, onSelect, weekSummary }) {
    const thisWeek = getWeekDays(getWeekStart(0));
    const nextWeek = getWeekDays(getWeekStart(1));

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <WeekPicker
                weekDays={thisWeek} label="Текущая неделя"
                summary={weekSummary}
                selectedDate={selectedDate} onSelect={onSelect}
            />
            <WeekPicker
                weekDays={nextWeek} label="Следующая неделя"
                summary={weekSummary}
                selectedDate={selectedDate} onSelect={onSelect}
            />
        </Box>
    );
}

// ─── Компонент: строка фотографа для выбора ───────────────────────────────────
function PhotographerPickRow({ photographer, selected, onToggle, isAdmin, onToggleAdmin, disabled }) {
    return (
        <Box sx={{
            display:      'flex',
            alignItems:   'center',
            gap:          1.2,
            px:           1.5,
            py:           1,
            borderRadius: 'var(--joy-radius-md)',
            border:       '1px solid',
            borderColor:  selected ? 'var(--joy-palette-primary-500)' : 'var(--joy-palette-divider)',
            bgcolor:      selected ? 'rgba(162,38,226,0.07)' : 'transparent',
            cursor:       disabled ? 'not-allowed' : 'pointer',
            opacity:      disabled ? 0.45 : 1,
            transition:   'all .15s',
            '&:hover':    !disabled ? { borderColor: 'var(--joy-palette-primary-400)' } : {},
        }}>
            {/* Чекбокс */}
            <Box
                onClick={() => !disabled && onToggle()}
                sx={{
                    width: 18, height: 18, flexShrink: 0,
                    borderRadius: '5px',
                    border: '1.5px solid',
                    borderColor: selected ? 'var(--joy-palette-primary-500)' : 'var(--joy-palette-neutral-500)',
                    bgcolor: selected ? 'var(--joy-palette-primary-500)' : 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all .15s',
                }}
            >
                {selected && <CheckRoundedIcon sx={{ fontSize: 12, color: '#fff' }} />}
            </Box>

            <Avatar
                src={photographer.avatar_url}
                size="sm"
                onClick={() => !disabled && onToggle()}
                sx={{
                    width: 28, height: 28, fontSize: 10, fontWeight: 700, flexShrink: 0,
                    bgcolor: roleMeta(photographer.role).bg,
                    color:   roleMeta(photographer.role).color,
                }}
            >
                {(photographer.first_name || '').charAt(0).toUpperCase()}
            </Avatar>

            <Box sx={{ flex: 1, minWidth: 0 }} onClick={() => !disabled && onToggle()}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: 13, fontWeight: selected ? 700 : 500, color: 'var(--joy-palette-text-primary)', lineHeight: 1.2 }}>
                        {photographer.first_name} {photographer.last_name}
                    </Typography>
                    {photographer.role !== 'photographer' && (
                        <Box sx={{ fontSize: 9, fontWeight: 800, color: roleMeta(photographer.role).color, bgcolor: roleMeta(photographer.role).bg, borderRadius: '4px', px: 0.5, py: 0.1 }}>
                            {roleMeta(photographer.role).label}
                        </Box>
                    )}
                </Box>
                {photographer.preferred_time && (
                    <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-primary-400)', display: 'flex', alignItems: 'center', gap: 0.3 }}>
                        <AccessTimeRoundedIcon sx={{ fontSize: 11 }} />
                        хочет с {photographer.preferred_time.slice(0, 5)}
                    </Typography>
                )}
            </Box>

            {/* Кнопка "Админ" — только если выбран */}
            {selected && (
                <Box
                    onClick={onToggleAdmin}
                    sx={{
                        display: 'flex', alignItems: 'center', gap: 0.4,
                        px: 0.8, py: 0.3,
                        borderRadius: 'var(--joy-radius-sm)',
                        border: '1px solid',
                        borderColor: isAdmin ? '#f59e0b' : 'var(--joy-palette-divider)',
                        bgcolor:     isAdmin ? 'rgba(245,158,11,0.1)' : 'transparent',
                        cursor: 'pointer', transition: 'all .15s',
                        '&:hover': { borderColor: '#f59e0b' },
                    }}
                >
                    {/* <StarRoundedIcon sx={{ fontSize: 12, color: isAdmin ? '#f59e0b' : 'var(--joy-palette-text-tertiary)' }} /> */}
                    <Typography sx={{ fontSize: 10, fontWeight: 600, color: isAdmin ? '#f59e0b' : 'var(--joy-palette-text-tertiary)' }}>
                        Админ
                    </Typography>
                </Box>
            )}
        </Box>
    );
}

// ─── Компонент: блок одной локации ────────────────────────────────────────────
function LocationBlock({ location, available, selectedMap, onToggle, onToggleAdmin, onTimeChange, index, bulkTime, onBulkTimeChange, onApplyBulkTime }) {
    const selected = selectedMap[location.id] || {};

    const assignedElsewhere = new Set();
    Object.entries(selectedMap).forEach(([locId, phMap]) => {
        if (String(locId) !== String(location.id)) {
            Object.keys(phMap).forEach(phId => assignedElsewhere.add(Number(phId)));
        }
    });
    const filteredAvailable = available.filter(ph => !assignedElsewhere.has(ph.id));

    return (
        <motion.div custom={index} variants={cardAnim} initial="hidden" animate="visible">
            <Box sx={{
                bgcolor:      'var(--joy-palette-background-level1)',
                border:       '1px solid var(--joy-palette-divider)',
                borderRadius: 'var(--joy-radius-xl)',
                overflow:     'hidden',
            }}>
                {/* Шапка */}
                <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5,
                    px: 2.5, py: 1.6,
                    borderBottom: Object.keys(selected).length > 0 || filteredAvailable.length > 0
                        ? '1px solid var(--joy-palette-divider)'
                        : 'none',
                    bgcolor: 'var(--joy-palette-background-level2)',
                }}>
                    <Box sx={{
                        width: 32, height: 32, borderRadius: 'var(--joy-radius-sm)',
                        bgcolor: 'rgba(162,38,226,0.12)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                        <LocationOnRoundedIcon sx={{ fontSize: 16, color: 'var(--joy-palette-primary-400)' }} />
                    </Box>
                    <Typography sx={{ fontSize: 15, fontWeight: 700, color: 'var(--joy-palette-text-primary)', flex: 1 }}>
                        {location.name}
                    </Typography>
                    {Object.keys(selected).length > 0 && (
                        <Chip size="sm" sx={{ bgcolor: 'rgba(162,38,226,0.12)', color: 'var(--joy-palette-primary-300)', fontWeight: 700, fontSize: 11 }}>
                            {Object.keys(selected).length} чел.
                        </Chip>
                    )}
                </Box>

                {/* Список доступных */}
                {filteredAvailable.length === 0 ? (
                    <Box sx={{ px: 2.5, py: 2, textAlign: 'center' }}>
                        <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)' }}>
                            Нет доступных фотографов на этот день
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ p: 1.5 }}>
                        <Stack spacing={0.7}>
                            {filteredAvailable.map(ph => {
                                const isSelected = !!selected[ph.id];
                                const isAdmin    = selected[ph.id]?.isAdmin || false;
                                return (
                                    <PhotographerPickRow
                                        key={ph.id}
                                        photographer={ph}
                                        selected={isSelected}
                                        isAdmin={isAdmin}
                                        onToggle={() => onToggle(location.id, ph)}
                                        onToggleAdmin={() => onToggleAdmin(location.id, ph.id)}
                                        disabled={false}
                                        fromAssignment={ph._from_assignment}
                                    />
                                );
                            })}
                        </Stack>

                        {/* Поле времени для выбранных */}
                        {Object.keys(selected).length > 0 && (
                            <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid var(--joy-palette-divider)' }}>
                                {/* Групповое время */}
                                {Object.keys(selected).length >= 2 && (
                                    <Box sx={{
                                        display: 'flex', alignItems: 'center', gap: 1,
                                        mb: 1.5, pb: 1.5,
                                        borderBottom: '1px solid var(--joy-palette-divider)',
                                    }}>
                                        <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'var(--joy-palette-text-tertiary)', whiteSpace: 'nowrap' }}>
                                            Всем:
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <AccessTimeRoundedIcon sx={{ fontSize: 14, color: 'var(--joy-palette-primary-400)' }} />
                                            <TimeSelect5
                                                value={bulkTime || '10:00'}
                                                onChange={(v) => onBulkTimeChange(v)}
                                            />
                                        </Box>
                                        <Button
                                            size="sm"
                                            variant="outlined"
                                            disabled={!bulkTime}
                                            onClick={() => onApplyBulkTime(bulkTime)}
                                            sx={{
                                                fontSize: 11, fontWeight: 700,
                                                borderColor: 'var(--joy-palette-primary-500)',
                                                color: 'var(--joy-palette-primary-300)',
                                                minHeight: 26, px: 1.5,
                                                '&:hover': { bgcolor: 'rgba(162,38,226,0.08)' },
                                                '&:disabled': { opacity: 0.4 },
                                            }}
                                        >
                                            Применить
                                        </Button>
                                    </Box>
                                )}
                                <Stack spacing={0.8}>
                                    {Object.entries(selected).map(([phId, info]) => {
                                        const ph = available.find(p => p.id === Number(phId));
                                        if (!ph) return null;
                                        return (
                                            <Box key={phId} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-secondary)', flex: 1, minWidth: 0 }}>
                                                    {ph.first_name} {ph.last_name}
                                                    {info.isAdmin && (
                                                        <Box component="span" sx={{ ml: 0.5, color: '#f59e0b', fontSize: 10 }}>Админ</Box>
                                                    )}
                                                </Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <AccessTimeRoundedIcon sx={{ fontSize: 14, color: 'var(--joy-palette-text-tertiary)' }} />
                                                    <TimeSelect5
                                                        value={info.time || ph.preferred_time?.slice(0,5) || '10:00'}
                                                        onChange={(v) => onTimeChange(location.id, Number(phId), v)}
                                                    />
                                                </Box>
                                            </Box>
                                        );
                                    })}
                                </Stack>
                            </Box>
                        )}
                    </Box>
                )}
            </Box>
        </motion.div>
    );
}

// ─── Компонент: превью итога ──────────────────────────────────────────────────
function SummaryPreview({ date, locations, selectedMap, available }) {
    const lines = locations.flatMap(loc => {
        const sel = selectedMap[loc.id] || {};
        return Object.entries(sel).map(([phId, info]) => {
            const ph = available.find(p => p.id === Number(phId));
            return { loc, ph, info };
        });
    }).filter(x => x.ph);

    if (lines.length === 0) return null;

    return (
        <Box sx={{
            bgcolor: 'rgba(162,38,226,0.06)',
            border: '1px solid rgba(162,38,226,0.25)',
            borderRadius: 'var(--joy-radius-xl)',
            p: 2.5,
        }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--joy-palette-primary-300)', mb: 1.5 }}>
                Итого на {fmtDateFull(date)}
            </Typography>
            <Stack spacing={0.7}>
                {locations.map(loc => {
                    const sel = selectedMap[loc.id] || {};
                    if (Object.keys(sel).length === 0) return null;
                    return (
                        <Box key={loc.id}>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'var(--joy-palette-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.4 }}>
                                {loc.name}
                            </Typography>
                            {Object.entries(sel)
                                .map(([phId, info]) => {
                                    const ph = available.find(p => p.id === Number(phId));
                                    return ph ? { phId, info, ph } : null;
                                })
                                .filter(Boolean)
                                .sort((a, b) => {
                                    // Сортируем по времени выхода (ascending), потом по имени
                                    const timeA = (a.info.time || a.ph.preferred_time?.slice(0,5) || '99:99');
                                    const timeB = (b.info.time || b.ph.preferred_time?.slice(0,5) || '99:99');
                                    if (timeA !== timeB) return timeA.localeCompare(timeB);
                                    return (a.ph.first_name || '').localeCompare(b.ph.first_name || '');
                                })
                                .map(({ phId, info, ph }) => (
                                    <Box key={phId} sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 1 }}>
                                        <Avatar
                                            src={ph.avatar_url}
                                            size="sm"
                                            sx={{ width: 22, height: 22, fontSize: 9, fontWeight: 700, flexShrink: 0 }}
                                        >
                                            {(ph.first_name || '').charAt(0).toUpperCase()}
                                        </Avatar>
                                        <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-primary)' }}>
                                            {ph.first_name} {ph.last_name}
                                            {info.isAdmin && (
                                                <Chip size="sm" sx={{
                                                    bgcolor: 'rgba(245,158,11,0.12)',
                                                    color: '#f59e0b',
                                                    fontWeight: 700,
                                                    fontSize: 9,
                                                    height: 18,
                                                    borderColor: 'rgba(245,158,11,0.3)',
                                                    border: '1px solid',
                                                }}>
                                                    Админ
                                                </Chip>
                                            )}
                                        </Typography>
                                        <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)', ml: 'auto' }}>
                                            с {info.time || ph.preferred_time?.slice(0,5) || '10:00'}
                                        </Typography>
                                    </Box>
                                )
                            )}
                        </Box>
                    );
                })}
            </Stack>
        </Box>
    );
}

// ════════════════════════════════════════════════════════════════════
//  AssignPage
// ════════════════════════════════════════════════════════════════════
export default function AssignPage() {
    const { token, showToast, hasPerm } = useContext(AppContext);
    const navigate    = useNavigate();
    const [params]    = useSearchParams();
    const [bulkTime, setBulkTime] = useState({});

    const canEdit = hasPerm('schedule.edit');
    
    const [date,         setDate]         = useState(params.get('date') || businessTodayISO());
    const [locations,    setLocations]    = useState([]);
    const [available,    setAvailable]    = useState([]);
    const [weekSummary, setWeekSummary] = useState({});  // { 'YYYY-MM-DD': { available, assigned } }
    const [existing,     setExisting]     = useState([]);
    // selectedMap: { locationId: { phId: { isAdmin, time } } }
    const [selectedMap,  setSelectedMap]  = useState({});
    const [loading,      setLoading]      = useState(false);
    const [saving,       setSaving]       = useState(false);
    const [confirming,   setConfirming]   = useState(false);
    const [confirmed,    setConfirmed]    = useState(false);
    const [error,        setError]        = useState('');
    
    const [editSchedOpen, setEditSchedOpen] = useState(false);

    // ── Загрузка данных при смене даты ────────────────────────────
    const loadForDate = useCallback(async (d) => {
        setLoading(true);
        setError('');
        setSelectedMap({});
        setConfirmed(false);

        const [locs, avail, exist] = await Promise.all([
            APIGetLocations(token),
            APIGetAvailableForDate(token, d),
            APIGetDayAssignments(token, d),
        ]);

        const locList   = Array.isArray(locs) ? locs : (locs?.results || []);
        const availList = Array.isArray(avail) ? avail : (avail?.available || []);
        const existList = Array.isArray(exist) ? exist : (exist?.assignments || []);

        // Уже назначенные фотографы должны быть видны в списке выбора, даже если
        // они НЕ оставили доступности на этот день (иначе мы их видим как
        // "1 назначено", но не можем редактировать/снять).
        const availMap = new Map(availList.map(p => [p.id, p]));
        existList.forEach(a => {
            if (!availMap.has(a.account)) {
                availMap.set(a.account, {
                    id:             a.account,
                    first_name:     a.account_name?.split(' ')[0] || a.account_name || '—',
                    last_name:      a.account_name?.split(' ').slice(1).join(' ') || '',
                    full_name:      a.account_name || '',
                    preferred_time: a.start_time || null,
                    // Помечаем что этот фотограф НЕ имеет официальной доступности —
                    // покажем серый флажок в UI.
                    _from_assignment: true,
                });
            }
        });
        const mergedAvailList = Array.from(availMap.values());

        setLocations(locList.filter(l => l.is_active));
        setAvailable(mergedAvailList);

        if (existList.length > 0) {
            const map = {};
            existList.forEach(a => {
                if (!map[a.location]) map[a.location] = {};
                map[a.location][a.account] = {
                    isAdmin:    a.is_admin,
                    time:       a.start_time?.slice(0, 5) || '10:00',
                    existingId: a.id,
                };
            });
            setSelectedMap(map);
            if (existList.some(a => a.is_confirmed)) setConfirmed(true);
        }

        setLoading(false);
    }, [token]);

    // ── Сводка по двум неделям (для счётчиков в пикере) ──
    // Берём готовый серверный week-summary: по каждому дню он отдаёт
    // available_count (доступно) и assigned_count (назначено). Один запрос
    // на неделю, без ручного пересчёта.
    const loadWeeksSummary = useCallback(async () => {
        if (!token) return;
        const startThis = getWeekStart(0);
        const startNext = getWeekStart(1);

        const [sumThis, sumNext] = await Promise.all([
            APIGetWeekSummary(token, toISO(startThis)),
            APIGetWeekSummary(token, toISO(startNext)),
        ]);

        const summaryMap = {};
        [sumThis, sumNext].forEach(resp => {
            (resp?.days || []).forEach(day => {
                summaryMap[day.date] = {
                    available: day.available_count || 0,
                    assigned:  day.assigned_count  || 0,
                    byRole:    day.available_by_role || { photographer: 0, admin: 0, admin_photographer: 0 },
                };
            });
        });

        setWeekSummary(summaryMap);
    }, [token]);

    useEffect(() => { loadWeeksSummary(); }, [loadWeeksSummary]);

    useEffect(() => { loadForDate(date); }, [date, loadForDate]);

    // ── Тогл фотографа в локации ──────────────────────────────────
    const handleToggle = (locationId, ph) => {
        setSelectedMap(prev => {
            const locMap = { ...(prev[locationId] || {}) };
            if (locMap[ph.id]) {
                delete locMap[ph.id];
            } else {
                // Проверяем: не назначен ли этот фотограф уже на другую точку
                for (const [otherLocId, otherMap] of Object.entries(prev)) {
                    if (String(otherLocId) !== String(locationId) && otherMap[ph.id]) {
                        const loc = locations.find(l => l.id === Number(otherLocId));
                        showToast(`${ph.full_name || ph.first_name} уже назначен на точку «${loc?.name || otherLocId}»`, 'warning');
                        return prev;
                    }
                }
                locMap[ph.id] = {
                    isAdmin: false,
                    time:    ph.preferred_time?.slice(0, 5) || '10:00',
                };
            }
            return { ...prev, [locationId]: locMap };
        });
        setConfirmed(false);
    };

    const handleToggleAdmin = (locationId, phId) => {
        setSelectedMap(prev => {
            const locMap = { ...(prev[locationId] || {}) };
            if (!locMap[phId]) return prev;
            locMap[phId] = { ...locMap[phId], isAdmin: !locMap[phId].isAdmin };
            return { ...prev, [locationId]: locMap };
        });
    };

    const handleTimeChange = (locationId, phId, time) => {
        setSelectedMap(prev => {
            const locMap = { ...(prev[locationId] || {}) };
            if (!locMap[phId]) return prev;
            locMap[phId] = { ...locMap[phId], time };
            return { ...prev, [locationId]: locMap };
        });
    };

    // ── Сохранить черновик назначений ─────────────────────────────
    const handleSave = async () => {
        setSaving(true);
        setError('');

        const assignments = [];
        Object.entries(selectedMap).forEach(([locationId, phMap]) => {
            Object.entries(phMap).forEach(([phId, info]) => {
                const ph = available.find(p => p.id === Number(phId));
                assignments.push({
                    location_id:  Number(locationId),
                    account_id:   Number(phId),
                    start_time:   info.time || null,
                    time_source:  ph?.preferred_time ? 'photographer' : 'manager',
                    is_admin:     info.isAdmin,
                });
            });
        });

        // if (assignments.length === 0) {
        //     setError('Выберите хотя бы одного фотографа');
        //     setSaving(false);
        //     return;
        // }

        const res = await APICreateAssignments(token, { date, assignments });
        if (!res?.error) {
            showToast('Назначения сохранены ✓');
            await loadForDate(date);
            loadWeeksSummary();
        } else {
            setError(parseApiError(res.error, 'Не удалось сохранить назначения'));
        }
        setSaving(false);
    };

    // ── Подтвердить день (отправить уведомления) ──────────────────
    const handleConfirm = async () => {
        setConfirming(true);
        const res = await APIConfirmDay(token, date);
        if (!res?.error) {
            setConfirmed(true);
            showToast('День подтверждён, уведомления отправлены 🔔');
        } else {
            setError(parseApiError(res.error, 'Не удалось подтвердить день'));
        }
        setConfirming(false);
    };

    const totalAssigned = Object.values(selectedMap).reduce((acc, m) => acc + Object.keys(m).length, 0);

    return (
        <motion.div
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            style={{ minHeight: '100vh', background: 'var(--joy-palette-background-body)' }}
        >
            <Header />

            <Box sx={{ maxWidth: 780, mx: 'auto', px: { xs: 2, sm: 3 }, py: 3 }}>

                {/* Заголовок */}
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.04 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                        <Box
                            onClick={() => navigate('/manager')}
                            sx={{
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                width: 36, height: 36,
                                borderRadius: 'var(--joy-radius-md)',
                                border: '1px solid var(--joy-palette-divider)',
                                bgcolor: 'var(--joy-palette-background-level1)',
                                cursor: 'pointer', transition: 'all .15s',
                                '&:hover': { borderColor: 'var(--joy-palette-primary-500)', bgcolor: 'rgba(162,38,226,0.08)' },
                            }}
                        >
                            <ArrowBackRoundedIcon sx={{ fontSize: 18, color: 'var(--joy-palette-text-secondary)' }} />
                        </Box>
                        <Box>
                            <Typography sx={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--joy-palette-text-primary)' }}>
                                Назначить смены
                            </Typography>
                            <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)', mt: 0.2 }}>
                                {fmtDateFull(date)}
                                {confirmed && (
                                    <Box component="span" sx={{ ml: 1, color: '#22c55e', fontWeight: 700 }}>
                                        ✓ Подтверждено
                                    </Box>
                                )}
                            </Typography>
                        </Box>
                    </Box>
                </motion.div>

                {/* Выбор даты */}
                <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.08 }}>
                    <Box sx={{
                        bgcolor: 'var(--joy-palette-background-level1)',
                        border: '1px solid var(--joy-palette-divider)',
                        borderRadius: 'var(--joy-radius-xl)',
                        p: { xs: 2, sm: 2.5 },
                        mb: 2.5,
                    }}>
                        <DatePicker
                            selectedDate={date}
                            onSelect={setDate}
                            weekSummary={weekSummary}
                        />
                    </Box>
                </motion.div>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <MemeLoader />
                    </Box>
                ) : (
                    <>
                        {/* Доступно фотографов */}
                        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.12 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                                <Chip
                                    size="sm"
                                    startDecorator={<PersonRoundedIcon sx={{ fontSize: 13 }} />}
                                    sx={{ bgcolor: 'rgba(162,38,226,0.1)', color: 'var(--joy-palette-primary-300)', fontWeight: 700, fontSize: 12 }}
                                >
                                    {available.filter(p => !p._from_assignment).length} доступно
                                </Chip>
                                <Chip
                                    size="sm"
                                    startDecorator={<LocationOnRoundedIcon sx={{ fontSize: 13 }} />}
                                    sx={{ bgcolor: 'rgba(162,38,226,0.07)', color: 'var(--joy-palette-text-secondary)', fontSize: 12 }}
                                >
                                    {locations.length} точек
                                </Chip>
                                {totalAssigned > 0 && (
                                    <Chip
                                        size="sm"
                                        startDecorator={<CheckRoundedIcon sx={{ fontSize: 13 }} />}
                                        sx={{ bgcolor: 'rgba(34,197,94,0.1)', color: '#22c55e', fontWeight: 700, fontSize: 12 }}
                                    >
                                        {totalAssigned} назначено
                                    </Chip>
                                )}
                                <Button
                                    size="sm"
                                    variant="outlined"
                                    onClick={() => setEditSchedOpen(true)}
                                    sx={{ ml: 'auto', fontWeight: 700 }}
                                >
                                    Изменить график
                                </Button>
                            </Box>
                        </motion.div>

                        {/* Блоки локаций */}
                        <Stack spacing={1.5}>
                            {locations.map((loc, i) => (
                                <LocationBlock
                                    key={loc.id}
                                    index={i}
                                    location={loc}
                                    available={available}
                                    selectedMap={selectedMap}
                                    onToggle={handleToggle}
                                    onToggleAdmin={handleToggleAdmin}
                                    onTimeChange={handleTimeChange}
                                    bulkTime={bulkTime[loc.id] || ''}
                                    onBulkTimeChange={(time) => setBulkTime(prev => ({ ...prev, [loc.id]: time }))}
                                    onApplyBulkTime={(time) => {
                                        setSelectedMap(prev => {
                                            const locMap = { ...(prev[loc.id] || {}) };
                                            Object.keys(locMap).forEach(phId => {
                                                locMap[phId] = { ...locMap[phId], time };
                                            });
                                            return { ...prev, [loc.id]: locMap };
                                        });
                                    }}
                                />
                            ))}
                        </Stack>

                        {/* Превью итога */}
                        {totalAssigned > 0 && (
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.3 }}
                                style={{ marginTop: 20 }}
                            >
                                <SummaryPreview
                                    date={date}
                                    locations={locations}
                                    selectedMap={selectedMap}
                                    available={available}
                                />
                            </motion.div>
                        )}

                        {/* Ошибка */}
                        <AnimatePresence>
                            {error && (
                                <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} style={{ marginTop: 16 }}>
                                    <Alert color="danger" variant="soft" size="sm" sx={{ borderRadius: 'var(--joy-radius-md)', fontSize: 13 }}>
                                        {error}
                                    </Alert>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* Кнопки действий */}
                        {canEdit && (
                            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.2 }} style={{ marginTop: 20 }}>
                                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                                    {/* Сохранить */}
                                    <Button
                                        onClick={handleSave}
                                        loading={saving}
                                        disabled={totalAssigned === 0 && existing.length === 0}
                                        sx={{
                                            background: 'linear-gradient(135deg, var(--joy-palette-primary-500) 0%, var(--joy-palette-primary-300) 100%)',
                                            color: '#fff', fontWeight: 700, fontSize: 13,
                                            border: 'none',
                                            boxShadow: '0 4px 16px rgba(162,38,226,0.35)',
                                            '&:hover': { boxShadow: '0 6px 24px rgba(162,38,226,0.5)' },
                                            '&:disabled': { opacity: 0.5 },
                                        }}
                                    >
                                        Сохранить назначения
                                    </Button>

                                    {/* Подтвердить и отправить уведомления */}
                                    {existing.length > 0 && (
                                        <Button
                                            variant={confirmed ? 'soft' : 'outlined'}
                                            onClick={handleConfirm}
                                            loading={confirming}
                                            disabled={confirmed}
                                            startDecorator={confirmed
                                                ? <CheckCircleRoundedIcon sx={{ fontSize: 16 }} />
                                                : <ArrowForwardRoundedIcon sx={{ fontSize: 16 }} />
                                            }
                                            sx={{
                                                borderColor: confirmed ? '#22c55e' : 'var(--joy-palette-primary-500)',
                                                color:       confirmed ? '#22c55e' : 'var(--joy-palette-primary-300)',
                                                fontWeight:  700, fontSize: 13,
                                                '&:hover': { bgcolor: confirmed ? 'rgba(34,197,94,0.08)' : 'rgba(162,38,226,0.08)' },
                                            }}
                                        >
                                            {confirmed ? 'Подтверждено' : 'Подтвердить и уведомить'}
                                        </Button>
                                    )}
                                </Box>
                            </motion.div>
                        )}
                    </>
                )}
            </Box>

            <ManagerScheduleEditModal
                open={editSchedOpen}
                date={date}
                onClose={() => setEditSchedOpen(false)}
                onSaved={() => { loadForDate(date); loadWeeksSummary(); }}
            />
        </motion.div>
    );
}